/**
 * 스코프 체크박스 매트릭스 — RESOURCES × ACTIONS 2차원 그리드 + 전역 `*` 토글.
 * taxonomy SSOT 는 `src/contracts/scope-runtime.ts` (zod 없이도 import 가능).
 */
import {
	ACTIONS as SCOPE_ACTIONS,
	RESOURCES as SCOPE_RESOURCES,
	type Action as ScopeAction,
	type Resource as ScopeResource
} from "../../../contracts/scope-runtime.ts"

export { scopeSatisfies, scopeSatisfiesAny } from "../../../contracts/scope-runtime.ts"
export { SCOPE_ACTIONS, SCOPE_RESOURCES, type ScopeAction, type ScopeResource }

export function ScopeMatrix(props: {
	readonly value: ReadonlyArray<string>
	readonly onChange: (next: ReadonlyArray<string>) => void
}) {
	const hasGlobal = props.value.includes("*")

	function toggleGlobal() {
		if (hasGlobal) props.onChange([])
		else props.onChange(["*"])
	}

	function hasResourceAll(r: ScopeResource): boolean {
		return props.value.includes(`${r}:*`)
	}

	function hasExact(r: ScopeResource, a: ScopeAction): boolean {
		if (hasGlobal) return true
		if (hasResourceAll(r)) return true
		return props.value.includes(`${r}:${a}`)
	}

	function toggleResourceAll(r: ScopeResource) {
		const key = `${r}:*`
		if (hasResourceAll(r)) {
			props.onChange(props.value.filter((s) => s !== key))
		} else {
			// 개별 {r}:{a} 항목 모두 제거 후 {r}:* 추가
			const filtered = props.value.filter((s) => s !== key && !SCOPE_ACTIONS.some((a) => s === `${r}:${a}`))
			props.onChange([...filtered, key])
		}
	}

	function toggleExact(r: ScopeResource, a: ScopeAction) {
		if (hasGlobal) return
		if (hasResourceAll(r)) return
		const key = `${r}:${a}`
		if (props.value.includes(key)) {
			props.onChange(props.value.filter((s) => s !== key))
		} else {
			props.onChange([...props.value, key])
		}
	}

	return (
		<div className="scope-matrix">
			<label className={`scope-global${hasGlobal ? " is-on" : ""}`}>
				<input type="checkbox" className="pk-check" checked={hasGlobal} onChange={toggleGlobal} />
				<span>
					<code className="inline">*</code> — 모든 권한 (admin)
				</span>
				<span className="hint">리소스·액션 체크를 한 번에 덮어쓴다</span>
			</label>

			<div className={`scope-matrix-grid${hasGlobal ? " is-masked" : ""}`}>
				<table>
					<thead>
						<tr>
							<th>리소스</th>
							{SCOPE_ACTIONS.map((a) => (
								<th key={a}>{a}</th>
							))}
							<th className="wildcard-head">
								<code className="inline">:*</code>
							</th>
						</tr>
					</thead>
					<tbody>
						{SCOPE_RESOURCES.map((r) => {
							const all = hasResourceAll(r)
							return (
								<tr key={r}>
									<td>
										<code className="inline">{r}</code>
									</td>
									{SCOPE_ACTIONS.map((a) => (
										<td key={a}>
											<input
												type="checkbox"
												className="pk-check"
												checked={hasExact(r, a)}
												disabled={all}
												onChange={() => toggleExact(r, a)}
												aria-label={`${r}:${a}`}
											/>
										</td>
									))}
									<td className="wildcard-cell">
										<input
											type="checkbox"
											className="pk-check"
											checked={all}
											onChange={() => toggleResourceAll(r)}
											aria-label={`${r}:*`}
										/>
									</td>
								</tr>
							)
						})}
					</tbody>
				</table>
			</div>
		</div>
	)
}
